<script lang="ts">
  // appState.ts
  import { inject, onMounted, provide, ref, type InjectionKey } from 'vue'
  import { FEATURE_FLAGS } from '@gat-ship/feature-flag'
  import { useDateNowInterval } from '@gat-ship/utils'
  import { useStartupState } from '../../composables/startup-state'
  import { useBuildInfo } from '../../composables/useBuildInfo'
  import type { UserServiceContext } from '@gat-ship/user'
  import { useUser } from '@gat-ship/user'

  export function createAppStateContext(
    startupState: ReturnType<typeof useStartupState>,
    _userContext: UserServiceContext
  ) {
    const { dateNow: todayUpdatedEveryMinute, resume } = useDateNowInterval(
      () => {},
      undefined,
      {
        interval: 60 * 1000,
        noLifecycleHooks: true,
        matchUsersTime: true
      }
    )

    resume()

    const appSidebarCollapsed = ref(false)

    function setAppSidebarCollapsed(value: boolean) {
      appSidebarCollapsed.value = value
    }

    const showDummyContent = ref(false)
    const { currentBuild } = useBuildInfo()

    onMounted(() => {
      if (!FEATURE_FLAGS.IS_DEV) {
        const wrenchIcon = String.fromCodePoint(0x1f527)
        if (!currentBuild.value) {
          console.warn('Build date not found.')
        }
        console.log(`${wrenchIcon} BUILD: ${currentBuild.value} ${wrenchIcon}`)
      }
    })

    return {
      showDummyContent,
      appSidebarCollapsed,
      setAppSidebarCollapsed,
      todayUpdatedEveryMinute,
      currentBuild,
      startupState
    }
  }

  export type AppState = ReturnType<typeof createAppStateContext>

  export const appStateKey = Symbol('gs-app-state') as InjectionKey<AppState>

  // Composable to inject the factory function and use it
  export function useAppState(): Omit<
    AppState,
    '_initializeApp' | 'startupState'
  > {
    const instance = inject(appStateKey)

    if (!instance) {
      throw new Error('App state not found')
    }

    return instance
  }

  export function _useAppState() {
    const instance = inject<AppState>(appStateKey)

    if (!instance) {
      throw new Error('App state not found')
    }

    return instance
  }
</script>
<script setup lang="ts">
  const startupState = useStartupState()
  const userContext = useUser()
  const appStateContext = createAppStateContext(startupState, userContext)
  provide(appStateKey, appStateContext)

  defineExpose(appStateContext)
</script>
<template>
  <slot />
</template>
